import { useEffect, useRef, useState } from "react";
import { Map as MapLibreMap, Marker } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { CARTO_STYLE_URL, neonizeCartoStyle } from "./map/gameStyle";
import { Wifi } from "lucide-react";

/**
 * Itinerario reale su MapLibre: tappe geolocalizzate in ordine, collegate da una linea neon,
 * colore per stato (completata/attiva/futura/annullata), rider live sulla tappa in consegna.
 * Le tappe senza posizione (es. servizi online) sono elencate a parte. Nessun indirizzo privato.
 */
export default function ExperienceRealMap({ steps = [], onOpen }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const [status, setStatus] = useState("loading");
  const [phase, setPhase] = useState("");

  const geoSteps = steps.filter((s) => typeof s.lat === "number" && typeof s.lng === "number" && !s.online);
  const onlineSteps = steps.filter((s) => s.online || (s.kind === "service" && (typeof s.lat !== "number")));

  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    let map;
    const center = geoSteps.length ? [geoSteps[0].lng, geoSteps[0].lat] : [7.628, 45.216];
    try {
      map = new MapLibreMap({ container: ref.current, style: CARTO_STYLE_URL, center, zoom: 13, pitch: 40, bearing: -18, attributionControl: false });
    } catch (e) { setStatus("error"); return; }
    const wd = setTimeout(() => { if (!map.isStyleLoaded()) setStatus("error"); }, 12000);
    map.on("load", () => {
      clearTimeout(wd);
      try { neonizeCartoStyle(map); } catch (e) {}
      draw(map);
      setStatus("ready");
      try { map.resize(); } catch (e) {}
    });
    map.on("error", () => {});
    mapRef.current = map;
    const t = setTimeout(() => { try { map.resize(); } catch (e) {} }, 400);
    return () => { clearTimeout(t); clearTimeout(wd); try { map.remove(); } catch (e) {} mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (mapRef.current && status === "ready") draw(mapRef.current); });

  function draw(map) {
    markersRef.current.forEach((m) => { try { m.remove(); } catch (e) {} });
    markersRef.current = [];
    // linea itinerario
    const coords = geoSteps.map((s) => [s.lng, s.lat]);
    const line = { type: "Feature", geometry: { type: "LineString", coordinates: coords } };
    if (map.getSource("exp-route")) { map.getSource("exp-route").setData(line); }
    else if (coords.length >= 2) {
      map.addSource("exp-route", { type: "geojson", data: line });
      map.addLayer({ id: "exp-route-glow", type: "line", source: "exp-route", layout: { "line-cap": "round", "line-join": "round" }, paint: { "line-color": "#00E676", "line-width": 7, "line-opacity": 0.18, "line-blur": 6 } });
      map.addLayer({ id: "exp-route-core", type: "line", source: "exp-route", layout: { "line-cap": "round", "line-join": "round" }, paint: { "line-color": "#7DF9FF", "line-width": 2.5, "line-opacity": 0.85 } });
    }
    // marker numerati
    geoSteps.forEach((s, i) => {
      const el = document.createElement("div");
      el.style.cssText = `width:34px;height:34px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:13px;color:#0A0A0C;cursor:pointer;border:2px solid ${s.step_color};background:${s.step_color};box-shadow:0 0 14px ${s.step_color}99;`;
      el.textContent = String(i + 1);
      el.setAttribute("data-testid", `map-marker-${i}`);
      if (s.step_key === "annullata") { el.style.background = "#3f3f46"; el.style.color = "#a1a1aa"; el.style.boxShadow = "none"; el.textContent = "✕"; }
      el.onclick = () => onOpen && onOpen(s);
      const mk = new Marker({ element: el }).setLngLat([s.lng, s.lat]).addTo(map);
      markersRef.current.push(mk);
    });
    // rider live sulla tappa in consegna
    const delivering = geoSteps.find((s) => s.kind === "product" && s.step_key === "consegna" && typeof s.rider_lat === "number");
    if (delivering) {
      const rel = document.createElement("div");
      rel.style.cssText = "width:26px;height:26px;border-radius:50%;background:#22D3EE;border:2px solid #0A0A0C;box-shadow:0 0 12px #22D3EE;display:flex;align-items:center;justify-content:center;font-size:13px;";
      rel.textContent = "🏍️";
      const mk = new Marker({ element: rel }).setLngLat([delivering.rider_lng, delivering.rider_lat]).addTo(map);
      markersRef.current.push(mk);
    }
    // fit bounds
    if (coords.length === 1) { map.easeTo({ center: coords[0], zoom: 14 }); }
    else if (coords.length >= 2) {
      const lons = coords.map((c) => c[0]); const lats = coords.map((c) => c[1]);
      try { map.fitBounds([[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]], { padding: 60, maxZoom: 15, duration: 600 }); } catch (e) {}
    }
  }

  return (
    <div>
      <div className="relative rounded-2xl overflow-hidden border border-white/10" style={{ height: 300 }} data-testid="experience-real-map">
        <div ref={ref} style={{ position: "absolute", inset: 0 }} />
        {status === "loading" && <div className="absolute inset-0 flex items-center justify-center bg-[#0A0A0C] text-zinc-500 text-sm">Carico la mappa…</div>}
        {status === "error" && <div className="absolute inset-0 flex items-center justify-center bg-[#0A0A0C] text-zinc-500 text-sm px-4 text-center">Mappa non disponibile. Le tappe sono elencate qui sotto.</div>}
      </div>
      {onlineSteps.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid="exp-online-steps">
          {onlineSteps.map((s, i) => (
            <span key={i} className="text-xs rounded-full px-3 py-1.5 bg-[#7DF9FF]/10 text-[#7DF9FF] border border-[#7DF9FF]/30 flex items-center gap-1"><Wifi className="h-3 w-3" /> {s.title || s.business_name} · online</span>
          ))}
        </div>
      )}
    </div>
  );
}
